/**
 * ScribeClickTracker
 *
 * This module is responsible for sending click data to Scribe.
 *
 * @module    scribe_click_tracker
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 */
import $ from 'jquery';
import _ from 'underscore';
import eventConstants from './scribe_event_constants';
import ScribeClickDataParser from './scribe_click_data_parser';

const {WEB_CLICK} = eventConstants;

const toTitleCase = str => {
  return str.replace(/(?:^|\s)\w/g, match => match.toUpperCase());
};

/**
 * Construct a ScribeClickTracker
 *
 * @class
 * @param {ScribeClient} client a scribe client to transmit data with
 */
class ScribeClickTracker {
  // Click location cookie TTL. Note our cookie storage takes the TTL in days
  TTL = (1 / 60) * (1 / 60) * (1 / 24); // 1 second in days

  constructor(config, client, ClickParser) {
    this.config = config;
    this.client = client;
    this.ClickParser = ClickParser || ScribeClickDataParser;
  }

  /**
   * Start tracking body clicks where an element has a click location data
   * attribute.
   *
   * @example
   * <a href="someplace.html"
   *    data-click-location="my_tracking_link"
   *    data-click-location-metadata="moar datas">...</a>
   */
  trackBodyClicks() {
    const $body = $('body');
    $body.off('click.scribe');
    $body.on(
      'click.scribe',
      '[data-click-location], .js-track-event',
      this._onBodyClick
    );
  }

  /**
   * This function uses arrow syntax because it is used as a callback, and we would
   * like __this__ to refer to the enclosing class
   *
   * @param event the event passed to this function when called as an event callback
   * @private
   */
  _onBodyClick = event => {
    // added because $(event.target) leads to errors if any parent elements have the attributes in trackedAttributes
    // but child elements do not
    const $clickedElement = $(
      this.constructor._getClickLocationElement(event.target)
    );
    if ($clickedElement !== null) {
      const triggerClientWebClick =
        $clickedElement.hasClass('js-track-event') &&
        !$clickedElement.data('click-location');

      const clickData = new this.ClickParser(
        this.config,
        $clickedElement
      ).parse();

      // Only track clicks that doesn't trigger a new page request here, for all other clicks, set a cookie and let server take
      // care of triggering the event
      if (triggerClientWebClick) {
        this._sendWebClick(clickData);

        if ($clickedElement.hasClass('js-stop-propagation-click')) {
          event.stopPropagation();
        }
      } else {
        this._setCookies(clickData);
      }
    }
  };

  _sendWebClick(clickData) {
    const event = _.extend({eventType: WEB_CLICK}, clickData);
    this.client.sendEvent(event);
  }

  /**
   * If an element does not have the attribute: data-click-location or the class: js-track-event,
   * try and search up to seven levels up for the attribute. Returns the element with the attribute, null otherwise
   *
   * @param node the clicked element
   *
   * @returns the node with the attribute present, null otherwise
   */
  static _getClickLocationElement(node) {
    const LEVEL = 7;
    let resultNode = node;
    for (let i = 0; i <= LEVEL; i++) {
      if (
        resultNode.hasAttribute('data-click-location') ||
        $(resultNode).hasClass('js-track-event')
      ) {
        return resultNode;
      } else {
        resultNode = resultNode.parentNode;
      }
    }
    return null;
  }

  _setCookies(clickData) {
    // Only set cookies that are not available from SPV2 cookies
    const requiredSubCookies = ['eventName', 'clickType', 'clickTag'];
    if (this.config.logScribeWebClickCookieSetUnset && this.config.logger) {
      this.config.logger.info({
        message: 'WebClick cookies set/unset',
        name: 'SCRIBE_CLIENT',
      });
    }

    this.config.cookieApi.setCookie('Scribe', this.config.transactionID, {
      field: 'RefTxid',
      expires: this.TTL,
    });

    _.each(requiredSubCookies, cookieName => {
      if (clickData[cookieName]) {
        this.config.cookieApi.setCookie('Scribe', clickData[cookieName], {
          field: toTitleCase(cookieName),
          expires: this.TTL,
        });
      }
    });
  }
}

export default ScribeClickTracker;
